/* lenz-badge-tooltip.js
 *
 * Tooltip de los badges informativos del producto ("A pedido", "Importación").
 * Funciona en desktop (hover/focus vía CSS puro) y mobile/touch (click toggle).
 *
 * Markup esperado (generado por snippets/product-badges.liquid):
 *   <button type="button" class="badge ... lenz-badge-tip"
 *           data-tooltip-text="Texto descriptivo del badge"
 *           aria-label="...">
 *     A pedido
 *   </button>
 *
 * Este script inyecta el <span class="lenz-badge-tip__pop"> con el texto y
 * maneja el toggle en touch. En desktop el hover funciona via CSS sin JS.
 *
 * Por qué tooltip via JS aunque hay CSS hover:
 *   - El media query `(hover: hover)` excluye correctamente touch devices,
 *     pero los browsers móviles requieren un click handler para el toggle.
 *   - Sin click handler, el usuario mobile no tendría forma de ver la info.
 *
 * Por qué inyectar el span via JS en lugar de Liquid:
 *   - Mantiene el HTML inicial chico (los tooltips son ~150 chars c/u).
 *   - Permite reusar la misma data-tooltip-text en cualquier elemento
 *     con .lenz-badge-tip sin duplicar markup.
 *   - El observer detecta nuevos badges inyectados dinámicamente (ej. al
 *     cambiar de variante el badge se re-renderea desde el on-sale-badge
 *     web component).
 */
(function () {
  'use strict';

  var POP_CLASS = 'lenz-badge-tip__pop';
  var TRIGGER_SELECTOR = '.lenz-badge-tip';
  var OPEN_CLASS = 'is-open';

  /**
   * Crea el elemento <span> del popover con el texto del tooltip.
   * Usa textContent (no innerHTML) para evitar XSS desde el data attribute.
   */
  function createPop(text) {
    var span = document.createElement('span');
    span.className = POP_CLASS;
    span.textContent = text;
    span.setAttribute('role', 'tooltip');
    return span;
  }

  /**
   * Reposiciona el tooltip horizontalmente si se sale del viewport.
   *
   * Caso típico: badge pegado al borde izquierdo del PDP mobile. El tooltip
   * (centrado sobre el badge con `left: 50%; transform: translateX(-50%)`)
   * intenta dibujarse 120px a la izquierda del badge — fuera del viewport.
   *
   * Estrategia: medir el rect del tooltip una vez visible. Si overflow por
   * izquierda/derecha, override `left` con un valor en `px` que mantenga el
   * tooltip dentro con 8px de margen. La flecha se reubica vía CSS variable
   * `--lenz-tip-arrow-x` para que SIGA apuntando al centro del badge (sin
   * eso la flecha se movería junto al tooltip y apuntaría al aire).
   */
  var VIEWPORT_PAD = 8;
  function repositionPop(trigger) {
    var pop = trigger.querySelector('.' + POP_CLASS);
    if (!pop) return;

    // Reset previo (importante si abrimos el mismo trigger en distintos scroll positions)
    pop.style.left = '';
    pop.style.right = '';
    pop.style.transform = '';
    pop.style.setProperty('--lenz-tip-arrow-x', '50%');

    // Forzar reflow para que el browser aplique los estilos default antes de medir
    var triggerRect = trigger.getBoundingClientRect();
    var popRect = pop.getBoundingClientRect();
    var viewportW = document.documentElement.clientWidth;

    // Si el tooltip cabe centrado, no tocamos nada
    if (popRect.left >= VIEWPORT_PAD && popRect.right <= viewportW - VIEWPORT_PAD) {
      return;
    }

    var triggerCenterX = triggerRect.left + triggerRect.width / 2;
    var popWidth = popRect.width;
    var newLeft;

    if (popRect.left < VIEWPORT_PAD) {
      // Overflow por izquierda: pegar el tooltip a 8px del edge izquierdo
      newLeft = VIEWPORT_PAD - triggerRect.left;
    } else {
      // Overflow por derecha: pegar a 8px del edge derecho
      newLeft = (viewportW - VIEWPORT_PAD - popWidth) - triggerRect.left;
    }

    pop.style.left = newLeft + 'px';
    pop.style.transform = 'translateY(0)';  // anular el translateX(-50%) default

    // Reubicar la flecha (::after) al centro del badge.
    // El offset es relativo al tooltip: arrow_x_in_pop = trigger_center - pop_left
    var arrowOffsetPx = triggerCenterX - (triggerRect.left + newLeft);
    // Clamp para no salirse del tooltip (margen 12px desde cada extremo)
    arrowOffsetPx = Math.max(12, Math.min(popWidth - 12, arrowOffsetPx));
    pop.style.setProperty('--lenz-tip-arrow-x', arrowOffsetPx + 'px');
  }

  /**
   * Asegura que el trigger tenga el popover dentro. Idempotente: si ya existe
   * uno no lo duplica. Si no hay data-tooltip-text, no hace nada.
   */
  function ensurePop(trigger) {
    if (!trigger || !trigger.querySelector) return null;
    var existing = trigger.querySelector('.' + POP_CLASS);
    if (existing) return existing;
    var text = trigger.getAttribute('data-tooltip-text');
    if (!text) return null;
    var pop = createPop(text);
    trigger.appendChild(pop);
    return pop;
  }

  /**
   * Cierra todos los tooltips abiertos excepto el indicado (si se pasa).
   */
  function closeAll(except) {
    var openTriggers = document.querySelectorAll(TRIGGER_SELECTOR + '.' + OPEN_CLASS);
    for (var i = 0; i < openTriggers.length; i++) {
      if (openTriggers[i] !== except) {
        openTriggers[i].classList.remove(OPEN_CLASS);
      }
    }
  }

  /**
   * Reposiciona el tooltip si se sale del viewport (mobile sobre todo: el
   * badge puede estar pegado a un borde y el tooltip de 220-240px se sale).
   *
   * Estrategia: leer la posición REAL del pop tras renderizar. Si su edge
   * izquierdo o derecho está fuera del viewport (con un margen mínimo de
   * 8px), aplicar un shift horizontal vía CSS variable --lenz-tip-shift.
   * Compensamos la flecha con --lenz-tip-arrow-x para que siga apuntando
   * al centro del badge.
   *
   * Se ejecuta tras añadir .is-open (con un microtask) y también en resize.
   */
  function reposition(trigger) {
    var pop = trigger.querySelector('.' + POP_CLASS);
    if (!pop) return;
    // Resetear shift previo para medir la posición "natural"
    pop.style.setProperty('--lenz-tip-shift', '0px');
    pop.style.setProperty('--lenz-tip-arrow-x', '50%');

    // Esperar 1 frame para que el browser pinte con los valores reseteados
    requestAnimationFrame(function () {
      var rect = pop.getBoundingClientRect();
      var vw = document.documentElement.clientWidth;
      var EDGE_MARGIN = 8; // mínimo aire entre tooltip y borde del viewport
      var shift = 0;
      if (rect.left < EDGE_MARGIN) {
        shift = EDGE_MARGIN - rect.left;
      } else if (rect.right > vw - EDGE_MARGIN) {
        shift = (vw - EDGE_MARGIN) - rect.right;
      }
      if (shift !== 0) {
        pop.style.setProperty('--lenz-tip-shift', shift + 'px');
        // Compensar la flecha: si el tooltip se movió `shift` px, la flecha
        // debe moverse en sentido CONTRARIO en su porcentaje para seguir
        // apuntando al centro del badge.
        // arrow_x_% = 50% - (shift / width) * 100
        var arrowPct = 50 - (shift / rect.width) * 100;
        // Clampear entre 12% y 88% para que la flecha no se salga del tooltip
        arrowPct = Math.max(12, Math.min(88, arrowPct));
        pop.style.setProperty('--lenz-tip-arrow-x', arrowPct + '%');
      }
    });
  }

  function handleClick(ev) {
    var trigger = ev.target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      // Click en el propio popover (que está DENTRO del button) — no togglear.
      // Permite seleccionar texto del tooltip sin que se cierre.
      if (ev.target.closest('.' + POP_CLASS)) return;

      ev.preventDefault();
      ev.stopPropagation();
      ensurePop(trigger);
      var wasOpen = trigger.classList.contains(OPEN_CLASS);
      // Cerrar OTROS tooltips (UX: solo uno abierto a la vez)
      closeAll(trigger);
      if (!wasOpen) {
        trigger.classList.add(OPEN_CLASS);
        reposition(trigger);
      } else {
        trigger.classList.remove(OPEN_CLASS);
      }
      return;
    }
    // Click fuera de cualquier tooltip → cerrar todos
    closeAll();
  }

  /**
   * En desktop el tooltip se muestra via :hover (CSS puro, sin pasar por
   * handleClick). Para reposicionarlo igual en desktop, agregamos un
   * listener mouseenter/focusin que dispara reposition() sin tocar el
   * estado .is-open (que es solo para mobile).
   */
  function handleHover(ev) {
    var trigger = ev.target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      ensurePop(trigger);
      reposition(trigger);
    }
  }

  function handleKeydown(ev) {
    if (ev.key === 'Escape' || ev.key === 'Esc') {
      closeAll();
    }
  }

  /**
   * Inicializa: crea popovers para todos los triggers existentes y monta
   * observers para nuevos triggers (ej. tras cambio de variante).
   */
  function init() {
    var triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    for (var i = 0; i < triggers.length; i++) {
      ensurePop(triggers[i]);
    }

    // Listeners globales (delegados). Usar bubble phase normal.
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeydown);
    // Hover en desktop: reposicionar si overflow (mouseenter no burbujea,
    // pero mouseover sí, y trabaja para event delegation).
    document.addEventListener('mouseover', handleHover);
    document.addEventListener('focusin', handleHover);

    // Reposicionar al resize/orientation change (caso: rotar mobile)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        var openTriggers = document.querySelectorAll(TRIGGER_SELECTOR + '.' + OPEN_CLASS);
        for (var i = 0; i < openTriggers.length; i++) reposition(openTriggers[i]);
      }, 100);
    });

    // MutationObserver para badges inyectados dinámicamente.
    // Caso típico: variant:change re-renderea el bloque de badges y los
    // botones son recreados desde cero — necesitan su popover de vuelta.
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          // El nodo mismo es un trigger
          if (node.classList && node.classList.contains('lenz-badge-tip')) {
            ensurePop(node);
          }
          // O contiene triggers nuevos
          if (node.querySelectorAll) {
            var inner = node.querySelectorAll(TRIGGER_SELECTOR);
            for (var k = 0; k < inner.length; k++) ensurePop(inner[k]);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
